import { useQuery } from 'react-query';
import { baseURL } from 'Types/APITypes';
import { CommercialNetworkServices, CommercialTireServices, CommercialMechanicalServices } from 'components/CommercialServices/CommercialServicesFields';
import { MaestroAPI, maestroApiCall } from '@michelin/central-provider';
import { Certificates } from 'components/CommercialServices/CommercialMechanicalServicesPanel';

const getCommercialTireServices = (stNumber: string|null) => {
  if (!stNumber) return null;
  return maestroApiCall({
    method: 'GET', endpoint: `locations/${stNumber}/commercial-services`, baseURL,
  }).then((data) => data.tire_services || {});
};

const getCommercialMechanicalServices = (stNumber: string|null) => {
  if (!stNumber) return null;
  return maestroApiCall({
    method: 'GET', endpoint: `locations/${stNumber}/commercial-services`, baseURL,
  }).then((data) => data.mechanical_services || {});
};

const getCommercialMechanicalServicesCertificates = (stNumber: string|null) => {
  if (!stNumber) return null;
  return maestroApiCall({
    method: 'GET', endpoint: `contacts/${stNumber}/certificates`, baseURL,
  }).then((data) => data || {});
};

const getCommercialNetworkServices = (stNumber: string|null) => {
  if (!stNumber) return null;
  return maestroApiCall({
    method: 'GET', endpoint: `locations/${stNumber}/commercial-services`, baseURL,
  }).then((data) => data.network || {});
};

export function useCommercialTireServices(locationNumber: string|null) {
  return useQuery<unknown, unknown, CommercialTireServices>(
    ['commercial-tire-services', locationNumber],
    () => getCommercialTireServices(locationNumber),
    {
      cacheTime: 3600,
      keepPreviousData: true,
    },
  );
}

export function useCommercialMechanicalServices(locationNumber: string|null) {
  return useQuery<unknown, unknown, CommercialMechanicalServices>(
    ['commercial-mechanical-services', locationNumber],
    () => getCommercialMechanicalServices(locationNumber),
    {
      cacheTime: 3600,
      keepPreviousData: true,
    },
  );
}

export function useCommercialMechanicalServicesCertificates(locationNumber: string|null) {
  return useQuery<unknown, unknown, Certificates>(
    ['commercial-mechanical-services-certificates', locationNumber],
    () => getCommercialMechanicalServicesCertificates(locationNumber),
    {
      cacheTime: 3600,
      keepPreviousData: true,
    },
  );
}

export function useCommercialNetworkServices(locationNumber: string|null) {
  return useQuery<unknown, unknown, CommercialNetworkServices>(
    ['commercial-networks', locationNumber],
    () => getCommercialNetworkServices(locationNumber),
    {
      cacheTime: 3600,
      keepPreviousData: true,
    },
  );
}

interface saveParams<T=any> {
  stNumber: string,
  data: T
}

export async function saveCommercialNetworkServices({ stNumber, data }: saveParams<CommercialNetworkServices>) {
  const apiType: MaestroAPI = { method: 'PUT', endpoint: `locations/${stNumber}/commercial-services/network`, baseURL };
  const response = await maestroApiCall(apiType, data).then((res) => res.data);
  return response;
}

export async function saveCommercialTireServices({ stNumber, data }: saveParams<CommercialTireServices>) {
  const apiType: MaestroAPI = { method: 'PUT', endpoint: `locations/${stNumber}/commercial-services/tire-services`, baseURL };
  const response = await maestroApiCall(apiType, data).then((res) => res.data);
  return response;
}

export async function saveCommercialMechanicalServices({ stNumber, data }: saveParams<CommercialMechanicalServices>) {
  const apiType: MaestroAPI = { method: 'PUT', endpoint: `locations/${stNumber}/commercial-services/mechanical-services`, baseURL };
  const response = await maestroApiCall(apiType, data).then((res) => res.data);
  return response;
}
